import React, { useState, useEffect, useRef } from 'react';
import jsQR from 'jsqr';

const MobileVinScanner = ({ onVinScanned, onError, onClose }) => {
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanningIntervalRef = useRef(null);

  // Simple VIN validation
  const isValidVIN = (vin) => {
    const cleanVin = vin.replace(/\s/g, '').toUpperCase();
    return cleanVin.length === 17 && /^[A-HJ-NPR-Z0-9]{17}$/.test(cleanVin);
  };

  // Full VIN validation with checksum (matches parent component)
  const validateVIN = (vin) => {
    const cleanVin = vin.replace(/\s/g, '').toUpperCase();
    
    if (cleanVin.length !== 17) {
      return { valid: false, error: 'VIN must be 17 characters long' };
    }
    
    const invalidChars = /[IOQ]/g;
    if (invalidChars.test(cleanVin)) {
      return { valid: false, error: 'VIN contains invalid characters (I, O, Q not allowed)' };
    }
    
    const validChars = /^[A-HJ-NPR-Z0-9]{17}$/;
    if (!validChars.test(cleanVin)) {
      return { valid: false, error: 'VIN contains invalid characters' };
    }
    
    // VIN checksum validation
    const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
    const transliteration = {
      'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8,
      'J': 1, 'K': 2, 'L': 3, 'M': 4, 'N': 5, 'P': 7, 'R': 9, 'S': 2,
      'T': 3, 'U': 4, 'V': 5, 'W': 6, 'X': 7, 'Y': 8, 'Z': 9
    };
    
    let sum = 0;
    for (let i = 0; i < 17; i++) {
      const char = cleanVin[i];
      let value;
      
      if (char >= '0' && char <= '9') {
        value = parseInt(char);
      } else {
        value = transliteration[char];
        if (value === undefined) {
          return { valid: false, error: 'VIN contains invalid characters' };
        }
      }
      
      sum += value * weights[i];
    }
    
    const checkDigit = sum % 11;
    const expectedCheckDigit = checkDigit === 10 ? 'X' : checkDigit.toString();
    const actualCheckDigit = cleanVin[8];
    
    if (actualCheckDigit !== expectedCheckDigit) {
      return { valid: false, error: 'Invalid VIN - check digit does not match' };
    }
    
    return { valid: true, cleanVin };
  };

  // Extract VIN from QR code data
  const extractVinFromQRCode = (qrData) => {
    // Pattern 1: Direct VIN (17 characters)
    const directVin = qrData.replace(/[^A-HJ-NPR-Z0-9]/g, '').toUpperCase();
    if (directVin.length === 17) {
      const validation = validateVIN(directVin);
      if (validation.valid) {
        return validation.cleanVin;
      }
    }
    
    // Pattern 2: Look for VIN pattern in the data
    const vinPattern = /[A-HJ-NPR-Z0-9]{17}/g;
    const matches = qrData.match(vinPattern);
    if (matches) {
      for (const match of matches) {
        const validation = validateVIN(match);
        if (validation.valid) {
          return validation.cleanVin;
        }
      }
    }
    
    // Pattern 3: Look for VIN with labels
    const labelPatterns = [
      /(?:VIN[:\s=]*)([A-HJ-NPR-Z0-9]{17})/gi,
      /"(?:vin|VIN)"[:\s]*"([A-HJ-NPR-Z0-9]{17})"/gi,
      /(?:vehicle|identification|number)[:\s]*([A-HJ-NPR-Z0-9]{17})/gi
    ];
    
    for (const pattern of labelPatterns) {
      const matches = qrData.match(pattern);
      if (matches) {
        for (const match of matches) {
          const vinMatch = match.match(/[A-HJ-NPR-Z0-9]{17}/i);
          if (vinMatch) {
            const validation = validateVIN(vinMatch[0]);
            if (validation.valid) {
              return validation.cleanVin;
            }
          }
        }
      }
    }
    
    return null;
  };

  // Handle detected QR code
  const handleDetectedCode = (qrData) => {
    const extractedVin = extractVinFromQRCode(qrData);
    
    if (extractedVin) {
      setScanResult({
        vin: extractedVin,
        success: true
      });
      
      stopScanning();
      
      // Auto-close and call callback
      setTimeout(() => {
        if (onVinScanned) {
          onVinScanned(extractedVin);
        }
      }, 1500);
    } else {
      setError('No valid VIN found in QR code. Please try again.');
      // Clear error after 3 seconds
      setTimeout(() => setError(''), 3000);
    }
  };

  // QR scanning logic
  const startQRScanning = () => {
    const video = videoRef.current;
    if (!video) return;
    
    scanningIntervalRef.current = setInterval(() => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          
          if (canvas.width === 0 || canvas.height === 0) return;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Try 4 different angles to handle flipped/rotated QR codes
          const angles = [0, 90, 180, 270];
          
          for (let angle of angles) {
            try {
              // Create a new canvas for this angle
              const rotatedCanvas = document.createElement('canvas');
              const rotatedCtx = rotatedCanvas.getContext('2d');
              
              // Set canvas size based on rotation
              if (angle === 90 || angle === 270) {
                rotatedCanvas.width = canvas.height;
                rotatedCanvas.height = canvas.width;
              } else {
                rotatedCanvas.width = canvas.width;
                rotatedCanvas.height = canvas.height;
              }
              
              // Apply rotation
              rotatedCtx.save();
              rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
              rotatedCtx.rotate((angle * Math.PI) / 180);
              rotatedCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
              rotatedCtx.restore();
              
              // Get image data for QR detection
              const imageData = rotatedCtx.getImageData(0, 0, rotatedCanvas.width, rotatedCanvas.height);
              
              // Try to detect QR code
              const qrResult = jsQR(imageData.data, imageData.width, imageData.height, {
                inversionAttempts: "dontInvert"
              });
              
              if (qrResult && qrResult.data) {
                handleDetectedCode(qrResult.data);
                return; // Exit the loop once found
              }
            } catch (error) {
              // Silent error handling for individual angle attempts
            }
          }
        } catch (error) {
          // Silent error handling
        }
      }
    }, 100);
  };

  // Initialize camera
  const initScanner = async () => {
    try {
      setIsScanning(true);
      setError('');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().then(() => {
            setTimeout(() => {
              startQRScanning();
            }, 500);
          });
        };
      }
    } catch (err) {
      setError('Failed to access camera. Please allow camera permissions.');
      setIsScanning(false);
    }
  };

  // Stop scanning
  const stopScanning = () => {
    if (scanningIntervalRef.current) {
      clearInterval(scanningIntervalRef.current);
      scanningIntervalRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setIsScanning(false);
  };

  // Initialize on mount
  useEffect(() => {
    initScanner();
    return () => stopScanning();
  }, []);

  // Handle close
  const handleClose = () => {
    stopScanning();
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white shadow-sm">
        <h2 className="text-lg font-bold text-gray-800">Scan VIN QR Code</h2>
        <button
          onClick={handleClose}
          className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center"
        >
          ×
        </button>
      </div>

      {/* Camera View */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {/* Video Container */}
        <div className="relative w-full max-w-sm mb-4">
          <video
            ref={videoRef}
            className="w-full h-64 bg-black rounded-lg object-cover"
            playsInline
            muted
            autoPlay
          />
          
          {/* QR Code Outline */}
          <div className="absolute inset-0 pointer-events-none">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <rect 
                x="20" y="25" width="60" height="50" 
                fill="none" stroke="#007AFF" strokeWidth="4" 
                rx="6" ry="6" strokeDasharray="12,6"
              >
                <animate attributeName="stroke-dashoffset" values="0;18" dur="2s" repeatCount="indefinite" />
              </rect>
              
              {/* Corner markers for better visibility */}
              <g stroke="#007AFF" strokeWidth="6" fill="none">
                <path d="M20,25 L20,35 M20,25 L30,25" />
                <path d="M80,25 L80,35 M80,25 L70,25" />
                <path d="M20,75 L20,65 M20,75 L30,75" />
                <path d="M80,75 L80,65 M80,75 L70,75" />
              </g>
            </svg>
          </div>
          
          {/* Success Overlay */}
          {scanResult && (
            <div className="absolute inset-0 bg-green-500 bg-opacity-90 flex items-center justify-center rounded-lg">
              <div className="text-white text-center p-4">
                <div className="text-5xl mb-3">✅</div>
                <p className="text-xl font-bold">VIN Found!</p>
                <p className="text-sm font-mono mt-2 bg-white bg-opacity-20 px-2 py-1 rounded">
                  {scanResult.vin}
                </p>
              </div>
            </div>
          )}
          
          {/* Scanner Status */}
          <div className="absolute bottom-2 left-2 right-2">
            <div className="bg-black bg-opacity-70 text-white text-sm px-3 py-2 rounded text-center">
              {isScanning ? (
                <div className="flex items-center justify-center">
                  <div className="animate-pulse w-3 h-3 bg-green-400 rounded-full mr-2"></div>
                  Scanning for QR code...
                </div>
              ) : (
                'Camera starting...'
              )}
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="w-full max-w-sm mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center text-sm">
              <span className="text-lg mr-2">📱</span>
              How to scan:
            </h3>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Point camera at QR code</li>
              <li>• Keep QR code within blue outline</li>
              <li>• Hold steady for 1-2 seconds</li>
              <li>• Works at any angle</li>
            </ul>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="w-full max-w-sm mb-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <span className="text-red-500 text-lg mr-2">⚠️</span>
                <div>
                  <p className="font-semibold text-red-900 text-sm">Error</p>
                  <p className="text-xs text-red-800">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="w-full max-w-sm space-y-3">
          {scanResult && (
            <button
              onClick={handleClose}
              className="w-full bg-green-600 text-white font-semibold py-4 px-6 rounded-lg text-base shadow-lg hover:bg-green-700 transition-colors"
            >
              Continue with VIN: {scanResult.vin}
            </button>
          )}

          <button
            onClick={handleClose}
            className="w-full bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-100 px-4 py-2 text-center">
        <p className="text-xs text-gray-600">
          🔒 Camera access is used only for QR code scanning
        </p>
      </div>
    </div>
  );
};

export default MobileVinScanner;
